/**
 * Buyer-facing theme tokens — ORG-SCOPED (organizations.buyer_theme), and
 * deliberately NOT the portal's per-user ThemeContext, which is a different
 * concept (logged-in staff preference, localStorage).
 *
 * Existing orgs resolve to `dark`, so nothing changes until a GM flips it.
 * The automotive demo is pinned dark regardless of the org setting.
 */
export const BUYER_THEMES = {
    dark: {
        name: 'dark',
        page: 'bg-neutral-950 text-neutral-100',
        header: 'backdrop-blur-xl bg-neutral-950/70 border-b border-white/5',
        dealership: 'text-amber-300/90',
        gallery: 'bg-neutral-900',
        scrim: 'bg-gradient-to-t from-neutral-950 via-neutral-950/70 to-transparent',
        title: 'text-white',
        accent: 'text-amber-300',
        price: 'text-amber-400',
        chip: 'bg-white/[0.04] border border-white/10 text-neutral-100',
        chipIcon: 'text-amber-400',
        section: 'bg-white/[0.03] border border-white/10 hover:bg-white/[0.05]',
        sectionTitle: 'text-neutral-100',
        panel: 'bg-white/[0.02] border border-white/10 divide-white/5',
        panelSolid: 'bg-white/[0.03] border border-white/10',
        label: 'text-neutral-500',
        body: 'text-neutral-100',
        muted: 'text-neutral-400',
        faint: 'text-neutral-600',
        dock: 'border-t border-white/10 bg-neutral-950/90 backdrop-blur-xl',
        sheet: 'bg-neutral-900 text-neutral-100 border-neutral-800',
        input: 'bg-neutral-800 border-neutral-600 text-neutral-100 placeholder:text-neutral-400',
        navIcon: 'text-neutral-300 hover:text-white',
        pickBtn: 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-neutral-100',
    },
    light: {
        name: 'light',
        page: 'bg-white text-slate-900',
        header: 'backdrop-blur-xl bg-white/85 border-b border-slate-200',
        dealership: 'text-slate-600',
        gallery: 'bg-slate-100',
        scrim: 'bg-gradient-to-t from-white via-white/75 to-transparent',
        title: 'text-slate-900',
        accent: 'text-slate-700',
        price: 'text-[#0B4FE0]',
        chip: 'bg-slate-100 border border-slate-200 text-slate-800',
        chipIcon: 'text-[#0B4FE0]',
        section: 'bg-slate-50 border border-slate-200 hover:bg-slate-100',
        sectionTitle: 'text-slate-900',
        panel: 'bg-white border border-slate-200 divide-slate-200',
        panelSolid: 'bg-slate-50 border border-slate-200',
        label: 'text-slate-500',
        body: 'text-slate-900',
        muted: 'text-slate-600',
        faint: 'text-slate-400',
        dock: 'border-t border-slate-200 bg-white/95 backdrop-blur-xl',
        sheet: 'bg-white text-slate-900 border-slate-200',
        input: 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400',
        navIcon: 'text-slate-600 hover:text-slate-900',
        pickBtn: 'border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-900',
    },
};

/** The automotive demo stays dark permanently, whatever the org is set to. */
export const resolveBuyerTheme = (orgTheme) => {
    const demo = typeof window !== 'undefined'
        && sessionStorage.getItem('yulontap_auto_demo') === '1';
    if (demo) return BUYER_THEMES.dark;
    return BUYER_THEMES[orgTheme === 'light' ? 'light' : 'dark'];
};

/** Tapelligence brand blue, sampled from /branding/tapelligence-logo.png. */
export const TAP_BLUE = '#0B4FE0';
export const TAP_BLUE_HOVER = '#0A45C4';
// Buyer-page accents. Orange carries "keep this for later" (Save / Send) and
// fuchsia carries Trade In, so it reads as its own destination rather than a
// second Finance & Insurance.
export const TAP_ORANGE = '#F5821F';
export const TAP_ORANGE_HOVER = '#DB7115';
export const TAP_FUCHSIA = '#D6006C';
export const TAP_FUCHSIA_HOVER = '#B8005C';
